import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  FluentProvider,
  Label,
  Spinner,
  type AccordionToggleEventHandler,
  tokens,
} from '@fluentui/react-components';
import { useActiveTheme } from '../theme/ThemeContext';
import {
  Add16Filled,
  ChevronRight20Regular,
  Delete20Filled,
  Edit20Filled,
} from '@fluentui/react-icons';
import type { IField, IMetadata } from '@moko/utils';
import { useOtherInfoContext } from './OtherInfoContext';
import { FieldList } from './FieldList';
import SlotSummary from '../CardList/SlotSummary';
import { buildCardData, resolveMetadataForItem } from '../CardList/cardUtils';

interface MobileEntityRepeatingPanelProps {
  field: IField;
  fields: IField[];
}

const getDisplayText = (entityInfo: string) =>
  entityInfo.split('|').splice(1).join('|');

const getPanelAddButtonLabel = (options: IField['options']) => {
  if (options && typeof options === 'object') {
    const addButtonLabel = (options as { addButtonLabel?: unknown })
      .addButtonLabel;
    return typeof addButtonLabel === 'string' && addButtonLabel.trim()
      ? addButtonLabel
      : undefined;
  }

  return undefined;
};

const EntityHeaderContent = memo(
  ({
    entityId,
    entityInfo,
    entityItems,
    entityMetadataMap,
  }: {
    entityId: number;
    entityInfo: string;
    entityItems?: Record<string, unknown>[];
    entityMetadataMap?: Record<string, IMetadata>;
  }) => {
    const fallback = getDisplayText(entityInfo);
    if (!entityItems || !entityMetadataMap) return <>{fallback}</>;
    const assetItem = entityItems.find((a) => a.id === entityId);
    if (!assetItem) return <>{fallback}</>;
    const assetMeta = resolveMetadataForItem(
      entityMetadataMap,
      'Asset',
      assetItem
    );
    if (!assetMeta?.cardLayout?.slots?.length) return <>{fallback}</>;
    const cardData = buildCardData(assetMeta, assetItem);
    return cardData.slots.length > 0 ? (
      <SlotSummary data={cardData} fallbackText={fallback} />
    ) : (
      <>{fallback}</>
    );
  }
);

export const MobileEntityRepeatingPanel = ({
  field,
  fields,
}: MobileEntityRepeatingPanelProps) => {
  const theme = useActiveTheme();
  const {
    params,
    currentElementInEntityRepeatingPanel,
    ensureEntityRepeatingEntry,
  } = useOtherInfoContext();

  const entities = useMemo(
    () => params.relatedEntities ?? [],
    [params.relatedEntities]
  );

  const entityItems = params.relatedEntityItems;
  const entityMetadataMap = params.entityMetadataMap;
  const [collapsedByUser, setCollapsedByUser] = useState(false);
  const prevEntityRef = useRef(currentElementInEntityRepeatingPanel);

  const readOnly = Boolean(params.readOnly);
  const allowAppend = field.allowAppend === true;
  const addButtonLabel =
    getPanelAddButtonLabel(field.options) ?? `Add New ${field.label}`;
  const contractorCreatedIds = params.contractorCreatedAssetIds;

  useEffect(() => {
    if (currentElementInEntityRepeatingPanel !== prevEntityRef.current) {
      setCollapsedByUser(false);
      prevEntityRef.current = currentElementInEntityRepeatingPanel;
    }
  }, [currentElementInEntityRepeatingPanel]);

  useEffect(() => {
    if (entities.length > 0 && !currentElementInEntityRepeatingPanel) {
      ensureEntityRepeatingEntry(field.name, entities[0].id);
    }
  }, [
    entities,
    currentElementInEntityRepeatingPanel,
    ensureEntityRepeatingEntry,
    field.name,
  ]);

  const activeId =
    currentElementInEntityRepeatingPanel ||
    (entities.length > 0 ? entities[0].id : 0);
  const openItems = collapsedByUser ? [] : activeId ? [String(activeId)] : [];

  const handleToggle: AccordionToggleEventHandler = useCallback(
    (_event, data) => {
      const items = data.openItems as string[];
      if (items.length > 0) {
        const newId = Number(items[0]);
        setCollapsedByUser(false);
        if (newId !== activeId) {
          ensureEntityRepeatingEntry(field.name, newId);
        }
      } else {
        setCollapsedByUser(true);
      }
    },
    [ensureEntityRepeatingEntry, field.name, activeId]
  );

  const handleEdit = useCallback(
    (e: React.MouseEvent, entityId: number) => {
      e.stopPropagation();
      params.onEditAsset?.(entityId);
    },
    [params]
  );

  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleRemove = useCallback((e: React.MouseEvent, entityId: number) => {
    e.stopPropagation();
    setConfirmDeleteId(entityId);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (confirmDeleteId == null) return;
    setDeleting(true);
    try {
      await params.onRemoveAsset?.(confirmDeleteId);
    } finally {
      setDeleting(false);
      setConfirmDeleteId(null);
    }
  }, [confirmDeleteId, params]);

  if (entities.length === 0 && !allowAppend) return null;

  return (
    <div
      id={'field_' + field.name}
      style={{
        width: '100%',
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: '5px',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '8px 10px',
          backgroundColor: tokens.colorBrandBackgroundInvertedSelected,
        }}
      >
        <Label style={{ fontWeight: 600, fontSize: '15px' }}>
          {field.label}
        </Label>
      </div>
      {entities.length > 0 && (
        <Accordion collapsible openItems={openItems} onToggle={handleToggle}>
          {entities.map((entity, index) => {
            const isDeletable = contractorCreatedIds?.has(entity.id) ?? false;
            return (
              <AccordionItem key={entity.id} value={String(entity.id)}>
                {index > 0 && (
                  <div
                    style={{
                      borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
                    }}
                  />
                )}
                <AccordionHeader
                  expandIconPosition="end"
                  style={{
                    backgroundColor:
                      tokens.colorBrandBackgroundInvertedSelected,
                  }}
                  button={{ style: { minHeight: 'auto', padding: '8px 10px' } }}
                  expandIcon={
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                    >
                      {!readOnly && isDeletable && (
                        <>
                          <Delete20Filled
                            style={{
                              cursor: 'pointer',
                              color: tokens.colorPaletteRedForeground1,
                              padding: 2,
                            }}
                            onClick={(e) =>
                              handleRemove(e as React.MouseEvent, entity.id)
                            }
                          />
                          <Edit20Filled
                            style={{
                              cursor: 'pointer',
                              color: tokens.colorBrandForeground1,
                              padding: 2,
                            }}
                            onClick={(e) =>
                              handleEdit(e as React.MouseEvent, entity.id)
                            }
                          />
                        </>
                      )}
                      <ChevronRight20Regular
                        style={{
                          transition: 'transform 0.2s',
                          transform: openItems.includes(String(entity.id))
                            ? 'rotate(90deg)'
                            : 'rotate(0deg)',
                        }}
                      />
                    </div>
                  }
                >
                  <EntityHeaderContent
                    entityId={entity.id}
                    entityInfo={entity.entityInfo}
                    entityItems={entityItems}
                    entityMetadataMap={entityMetadataMap}
                  />
                </AccordionHeader>
                <AccordionPanel>
                  {currentElementInEntityRepeatingPanel === entity.id && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        rowGap: '14px',
                        padding: '10px 0',
                      }}
                    >
                      <FieldList fields={fields} parentName={field.name} />
                    </div>
                  )}
                </AccordionPanel>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
      {allowAppend && !readOnly && (
        <div style={{ padding: '8px' }}>
          <Button
            appearance="primary"
            icon={<Add16Filled />}
            onClick={() => params.onAddAsset?.()}
            style={{ width: '100%' }}
          >
            {addButtonLabel}
          </Button>
        </div>
      )}

      <FluentProvider theme={theme}>
        <Dialog
          open={confirmDeleteId != null}
          onOpenChange={(_, data) => {
            if (!data.open && !deleting) setConfirmDeleteId(null);
          }}
        >
          <DialogSurface>
            <DialogBody>
              <DialogTitle>Remove asset?</DialogTitle>
              <DialogContent>
                This asset will be unlinked. Are you sure?
              </DialogContent>
              <DialogActions>
                <Button
                  appearance="secondary"
                  onClick={() => setConfirmDeleteId(null)}
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  onClick={handleConfirmDelete}
                  disabled={deleting}
                  icon={deleting ? <Spinner size="tiny" /> : undefined}
                >
                  {deleting ? 'Removing...' : 'Remove'}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      </FluentProvider>
    </div>
  );
};
